import { useQuery } from '@tanstack/react-query'
import { Link2, PhoneCall } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Card, CardContent } from '@/components/ui/card'
import { DateInput as DateField } from '@/components/ui/date-input'
import { Pagination } from '@/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DirectionIcon,
  DispositionChip,
  OutcomeBadge,
  RecordingPlayer,
  TrackingBadge,
} from '@/features/calling/components/CallBadges'
import { LinkCallDialog } from '@/features/calling/components/LinkCallDialog'
import { useCallingMe, useCallLog, useDispositions } from '@/hooks/useCalling'
import { useUrlState } from '@/hooks/useUrlState'
import { getWorkspaceUsers } from '@/services/api/task.service'
import { useCallStore } from '@/stores/call.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { Call, CallLogFilters, CallOutcome } from '@/types/api/calling'
import { formatDateTime } from '@/utils/date'

const OUTCOMES: { value: CallOutcome; label: string }[] = [
  { value: 'ANSWERED', label: 'Answered' },
  { value: 'NO_ANSWER', label: 'No answer / missed' },
  { value: 'BUSY', label: 'Busy' },
  { value: 'NOT_CONNECTED', label: 'Not connected (phone)' },
  { value: 'AGENT_UNAVAILABLE', label: "Agent didn't answer" },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELED', label: 'Canceled' },
  { value: 'UNKNOWN', label: 'Result unknown' },
]

const ALL = 'all'

/** Workspace call log: everything for managers (call:view_all), otherwise the user's own calls. */
export function CallLogPage() {
  const { data: me } = useCallingMe()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const [url, setUrl] = useUrlState('', {
    page: 0,
    size: 25,
    agent: ALL,
    outcome: ALL,
    direction: ALL,
    provider: ALL,
    disposition: ALL,
    from: '',
    to: '',
    unlinked: false as boolean,
  })
  const [linking, setLinking] = useState<Call | null>(null)
  const openDisposition = useCallStore((s) => s.openDisposition)

  const filters = useMemo<CallLogFilters>(() => ({
    agentUserId: url.agent !== ALL ? url.agent : undefined,
    outcome: url.outcome !== ALL ? (url.outcome as CallOutcome) : undefined,
    direction: url.direction !== ALL ? (url.direction as CallLogFilters['direction']) : undefined,
    providerType: url.provider !== ALL ? (url.provider as CallLogFilters['providerType']) : undefined,
    dispositionId: url.disposition !== ALL ? url.disposition : undefined,
    from: url.from ? new Date(`${url.from}T00:00:00`).toISOString() : undefined,
    to: url.to ? new Date(`${url.to}T23:59:59.999`).toISOString() : undefined,
    unlinked: url.unlinked || undefined,
  }), [url])

  const { data, isLoading } = useCallLog(filters, url.page, url.size)
  const { data: dispositions = [] } = useDispositions(true)
  const { data: members = [] } = useQuery({
    queryKey: ['workspace', workspaceId, 'workspace-users'],
    queryFn: getWorkspaceUsers,
    enabled: !!me?.canViewAll,
    staleTime: 5 * 60 * 1000,
  })

  const filter = (patch: Partial<typeof url>) => setUrl({ ...patch, page: 0 })

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold"><PhoneCall className="h-7 w-7 text-lime-600" /> Calls</h1>
        <p className="mt-1 text-neutral-500">{me?.canViewAll ? 'Every call in this workspace.' : 'Your calls.'}</p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        {me?.canViewAll && (
          <FilterSelect label="Agent" value={url.agent} onChange={(agent) => filter({ agent })}
            options={members.map((m) => ({ value: m.userId, label: m.name || m.email }))} />
        )}
        <FilterSelect label="Outcome" value={url.outcome} onChange={(outcome) => filter({ outcome })} options={OUTCOMES} />
        <FilterSelect label="Direction" value={url.direction} onChange={(direction) => filter({ direction })}
          options={[{ value: 'OUTBOUND', label: 'Outbound' }, { value: 'INBOUND', label: 'Inbound' }]} />
        <FilterSelect label="Mode" value={url.provider} onChange={(provider) => filter({ provider })}
          options={[{ value: 'EXOTEL', label: 'Click-to-call' }, { value: 'MOBILE_SIM', label: 'Phone (SIM)' }]} />
        <FilterSelect label="Disposition" value={url.disposition} onChange={(disposition) => filter({ disposition })}
          options={dispositions.map((d) => ({ value: d.id, label: d.label }))} />
        <DateInput label="From" value={url.from} onChange={(from) => filter({ from })} />
        <DateInput label="To" value={url.to} onChange={(to) => filter({ to })} />
        <label className="flex h-9 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 text-sm">
          <input type="checkbox" checked={url.unlinked} onChange={(e) => filter({ unlinked: e.target.checked })} />
          Unknown callers
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : !data || data.content.length === 0 ? (
            <p className="py-16 text-center text-sm text-neutral-500">No calls match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-neutral-100 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-4 py-2 font-medium">Lead</th>
                    <th className="px-4 py-2 font-medium">Agent</th>
                    <th className="px-4 py-2 font-medium">Result</th>
                    <th className="px-4 py-2 font-medium">Disposition</th>
                    <th className="px-4 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {data.content.map((call) => (
                    <tr key={call.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-3 tabular-nums text-neutral-600">
                        <div className="flex items-center gap-2"><DirectionIcon call={call} />{formatDateTime(call.requestedAt)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {call.recordId && call.recordGroupId ? (
                          <Link to={`/record-groups/${call.recordGroupId}/records/${call.recordId}`} className="font-medium text-neutral-900 hover:underline">
                            {call.leadName ?? 'Untitled record'}
                          </Link>
                        ) : (
                          <span className="font-medium text-neutral-500">Unknown caller</span>
                        )}
                        <p className="text-xs tabular-nums text-neutral-500">{call.leadNumber}</p>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">{call.agentName ?? '—'}</td>
                      <td className="space-y-1 px-4 py-3">
                        <div className="flex flex-wrap gap-1"><OutcomeBadge call={call} /><TrackingBadge call={call} /></div>
                        <RecordingPlayer call={call} />
                      </td>
                      <td className="px-4 py-3"><DispositionChip call={call} /></td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        {!call.recordId && call.canDispose && (
                          <button type="button" onClick={() => setLinking(call)} className="mr-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                            <Link2 className="h-3.5 w-3.5" /> Link to record
                          </button>
                        )}
                        {call.canDispose && call.status === 'ENDED' && (
                          <button type="button" onClick={() => openDisposition(call.id)} className="text-xs font-semibold text-primary-600 hover:underline">
                            {call.dispositionId ? 'Edit' : 'Add disposition'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {data && (
        <Pagination
          page={data.page}
          size={data.size}
          totalElements={data.totalElements}
          totalPages={data.totalPages}
          onPageChange={(page) => setUrl({ page })}
          onSizeChange={(size) => setUrl({ size, page: 0 })}
          itemLabel="calls"
        />
      )}
      {linking && <LinkCallDialog call={linking} onClose={() => setLinking(null)} />}
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div className="space-y-1">
      <span className="text-[11px] font-medium text-neutral-500">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-40" aria-label={label}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="space-y-1">
      <span className="block text-[11px] font-medium text-neutral-500">{label}</span>
      <DateField value={value} onChange={onChange} aria-label={`${label} date`} className="w-36" />
    </label>
  )
}
